/**
 * A validação de cada passo do wizard, num lugar só.
 *
 * As regras já existiam — `validacao.ts` e `datas.ts` portaram
 * `cteatf_validacao.dart` na 0034 —, mas **nada as ligava ao rodapé**:
 * `WizardRodape` implementa `desabilitado` e `motivoBloqueio` desde então e
 * ninguém os passava, o stepper deixava pular para qualquer passo, e por isso
 * dava para chegar à revisão com o formulário vazio. O erro aparecia como texto
 * cinza dentro do passo e o "Continuar" seguia clicável.
 *
 * Duas funções tinham perdido o consumidor quando a 0035 reescreveu os passos
 * de condutor e veículo. Em vez de apagá-las — regra portada do app não se joga
 * fora —, este módulo puxa o fio que faltava.
 */

import { erroDeDatas } from './datas';
import type { HoraDoDia } from './datas';
import { erroDaRota, erroDosPassageiros, erroDoTrajeto } from './validacao';
import type { WizardState } from './types';

/** `YYYY-MM-DD` + `HH:MM` sem passar por fuso — a string já é local. */
function paraData(dia: string, hora: string): { data: Date; hora: HoraDoDia } | null {
  const d = dia.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const t = hora.match(/^(\d{2}):(\d{2})$/);
  if (!d || !t) return null;
  return {
    data: new Date(Number(d[1]), Number(d[2]) - 1, Number(d[3])),
    hora: { hour: Number(t[1]), minute: Number(t[2]) },
  };
}

interface Contexto {
  estado: WizardState;
  permitirReduzirKm: boolean;
  /** Há veículo apto na lista; vazia é problema de cadastro, não de preenchimento. */
  temVeiculos: boolean;
  capacidade?: number | null;
}

/**
 * O que impede avançar deste passo. `null` = pode seguir.
 *
 * A ordem segue a da tela: condutor, veículo, rota, datas, trajeto,
 * passageiros, valor. A revisão (índice 7) não valida — ela mostra.
 */
export function erroDoPasso(passo: number, ctx: Contexto): string | null {
  const e = ctx.estado;

  switch (passo) {
    case 0:
      return e.idCondutorSgtf == null ? 'Selecione um condutor' : null;

    case 1:
      if (!ctx.temVeiculos) return 'Nenhum veículo do seu grupo está liberado para emissão. Contate o suporte.';
      // `idVeiculoSgtf` nunca é nulo com veículo escolhido: a lista é a
      // interseção dos dois cadastros (0035).
      return e.idVeiculo == null ? 'Selecione um veículo' : null;

    case 2:
      return erroDaRota({
        municipioOrigemIndice: e.municipioOrigemIndice,
        // O de-para do DER é conferido no próprio passo, que só permite
        // escolher da lista de sugestões.
        municipioOrigemAtfOk: true,
        municipioDestinoIndice: e.municipioDestinoIndice,
        municipioDestinoAtfOk: true,
        cMunIni: e.cMunIni,
        cMunFim: e.cMunFim,
      });

    case 3: {
      const ida = paraData(e.dataIda, e.horarioIda);
      if (!ida) return 'Informe a data e o horário de ida';
      if (e.somenteIda) return null;
      const volta = paraData(e.dataVolta, e.horarioVolta);
      if (!volta) return 'Informe a data e o horário de volta';
      return erroDeDatas({
        ida: ida.data, horaIda: ida.hora,
        volta: volta.data, horaVolta: volta.hora,
        somenteIda: false,
      });
    }

    case 4:
      return erroDoTrajeto({
        kmIda: e.kmIda, kmVolta: e.kmVolta,
        kmIdaBase: e.kmIdaBase, kmVoltaBase: e.kmVoltaBase,
        somenteIda: e.somenteIda,
        permitirReduzirKm: ctx.permitirReduzirKm,
        itinerario: e.itinerario,
      });

    case 5: {
      if (e.tomaNome.trim() === '') return 'Informe o contratante';
      if (e.tomaCpfCnpj.replace(/\D/g, '') === '') return 'Informe o CPF ou CNPJ do contratante';
      const erroPax = erroDosPassageiros(e.passageiros);
      if (erroPax) return erroPax;
      // Acima da capacidade o portal recusa — melhor barrar aqui do que depois
      // de o documento fiscal existir.
      if (ctx.capacidade != null && e.passageiros.length > ctx.capacidade) {
        return `Acima da capacidade do veículo (${ctx.capacidade} lugares)`;
      }
      return null;
    }

    case 6:
      return e.valorCentavos <= 0 ? 'Informe o valor da viagem' : null;

    default:
      return null;
  }
}

/**
 * O primeiro passo que ainda não pode ser deixado.
 *
 * Serve ao stepper: pular para o passo 5 com o 2 em branco levava à revisão com
 * dado faltando, e a recusa só aparecia depois — às vezes depois de o CT-e já
 * existir.
 */
export function primeiroPassoIncompleto(ctx: Contexto, ateExclusive: number): number | null {
  for (let p = 0; p < ateExclusive; p += 1) {
    if (erroDoPasso(p, ctx) != null) return p;
  }
  return null;
}
